import { useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { isUserAdmin, isContributorPerabiru } from '../../../utils/auth';

import L from 'leaflet';

import BaseLayer from './BaseLayer';
import OverlayGroup from './OverlayGroup';
import QuickOverlayLayers from './QuickOverlayLayers';
import BeFilterModal from './BeFilterModal';
import { overLayers } from '../config/panelLayers';
import {
	handleCheckboxChange,
	loadGeoJson,
	handleBaseLayerChange,
	getBeFilterLayerKey,
	loadAvailableBes,
	loadGeoJsonByBe,
	removeBeFilterLayer,
} from '../services/layerService';
import { initLayerService } from '../services/zIndexLayerDefine';
import useBlockMapScroll from '../../../hooks/useBlockMapScroll';

import './style/CustomPanel.css';

const BE_FILTER_LABEL = 'Filtrar por BE';

const CustomPanel = ({
	enableDrawerToggle,
	openBottomDrawer,
	onPendingReviewOpen,
}) => {
	const [expandedGroups, setExpandedGroups] = useState({});
	const map = useMap();
	const navigate = useNavigate();
	const loadedLayers = useRef({});
	const panelRef = useRef(null);
	const checkboxesRef = useRef([]);
	const [checkedLayers, setCheckedLayers] = useState({});
	const [selectedBaseLayer, setSelectedBaseLayer] = useState('OpenStreetMap');
	const [expandedSubmenus, setExpandedSubmenus] = useState({});
	const loadedBoundsCache = useRef({});
	const [loadingLayers, setLoadingLayers] = useState({});
	const beOptionsCache = useRef({});
	const beUpdateRunningRef = useRef({});
	const pendingBeSelectionRef = useRef({});
	const activeBeFilterRef = useRef({});
	const [selectedBesByLayer, setSelectedBesByLayer] = useState({});
	const [beUpdatingLayers, setBeUpdatingLayers] = useState({});
	const [beModal, setBeModal] = useState({
		open: false,
		layerName: null,
		options: [],
		loading: false,
	});
	const dispatch = useDispatch();
	const zIndex = useSelector(state => state.zIndex.currentZIndex);
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	const [visible, setVisible] = useState(!isMobile);
	useBlockMapScroll('.painel-filtro');
	const ADMIN_ONLY_LAYERS = new Set(['Caminho do Peabiru']);

	useEffect(() => {
		initLayerService(dispatch, zIndex);
	}, [dispatch, zIndex]);

	const getLayerConfig = layerName =>
		overLayers
			.flatMap(group => group.layers)
			.find(layer => layer.name === layerName);

	const getBeCheckboxKey = layerName =>
		`${layerName}::${BE_FILTER_LABEL}`;

	const getNormalChildren = layerGroup =>
		layerGroup?.children?.filter(child => child.type !== 'be-filter') || [];

	const toggleSubmenu = layerName => {
		setExpandedSubmenus(prev => ({
			...prev,
			[layerName]: !prev[layerName],
		}));
	};

	useEffect(() => {
		const handleResize = () => {
			const mobile = window.innerWidth <= 768;
			setIsMobile(mobile);

			// se for desktop, painel sempre visível
			if (!mobile) setVisible(true);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const panel = panelRef.current;
		if (!panel) return;

		L.DomEvent.disableClickPropagation(panel);
		L.DomEvent.disableScrollPropagation(panel);

		const stopTouchPropagation = e => {
			e.stopPropagation();
		};
		panel.addEventListener('touchstart', stopTouchPropagation, {
			passive: true,
		});
		panel.addEventListener('touchmove', stopTouchPropagation, {
			passive: true,
		});

		panel.style.zIndex = '1200';

		return () => {
			panel.removeEventListener('touchstart', stopTouchPropagation);
			panel.removeEventListener('touchmove', stopTouchPropagation);
		};
	}, [visible]);

	const toggleGroup = group => {
		setExpandedGroups(prev => ({
			...prev,
			[group]: !prev[group],
		}));
	};

	const ativarTodosSubLotes = useCallback(() => {
		const grupo = overLayers
			.flatMap(g => g.layers)
			.find(l => l.name === 'Lotes do Projeto');
		if (!grupo || !grupo.children) return;

		setCheckedLayers(prev => {
			const updated = { ...prev, 'Lotes do Projeto': true };
			grupo.children.forEach(child => {
				const fullName = `Lotes do Projeto::${child.name}`;
				updated[fullName] = true;
			});
			return updated;
		});

		grupo.children.forEach(child => {
			const fullName = `Lotes do Projeto::${child.name}`;
			const fakeEvent = {
				target: { checked: true },
				stopPropagation: () => {},
			};
			handleCheckboxChange(
				fakeEvent,
				map,
				loadedLayers,
				navigate,
				fullName
			);
		});
	}, [map, navigate]);

	const disableProductStatuses = async productName => {
		const layerGroup = getLayerConfig(productName);
		const statusChildren = getNormalChildren(layerGroup);

		await Promise.all(
			statusChildren.map(child => {
				const fullName = `${productName}::${child.name}`;
				const fakeEvent = {
					target: { checked: false },
					stopPropagation: () => {},
				};

				return handleCheckboxChange(
					fakeEvent,
					map,
					loadedLayers,
					navigate,
					fullName,
					{ silent: true, onPendingReviewOpen },
					true,
					openBottomDrawer
				);
			})
		);

		setCheckedLayers(prev => {
			const updated = { ...prev, [productName]: false };
			statusChildren.forEach(child => {
				updated[`${productName}::${child.name}`] = false;
			});
			return updated;
		});
	};

	const deactivateBeFilter = productName => {
		activeBeFilterRef.current[productName] = false;
		delete pendingBeSelectionRef.current[productName];

		removeBeFilterLayer(productName, map, loadedLayers);
		delete loadedBoundsCache.current[getBeFilterLayerKey(productName)];

		setSelectedBesByLayer(prev => ({
			...prev,
			[productName]: [],
		}));

		setCheckedLayers(prev => ({
			...prev,
			[getBeCheckboxKey(productName)]: false,
		}));

		setBeModal(prev =>
			prev.layerName === productName
				? {
						open: false,
						layerName: null,
						options: [],
						loading: false,
					}
				: prev
		);
	};

	const handleBeFilterToggle = async (e, productName) => {
		e.stopPropagation();

		const isChecked = e.target.checked;
		const checkboxKey = getBeCheckboxKey(productName);

		if (!isChecked) {
			deactivateBeFilter(productName);
			return;
		}

		activeBeFilterRef.current[productName] = true;

		setCheckedLayers(prev => ({
			...prev,
			[checkboxKey]: true,
		}));

		// O modo BE substitui os filtros por status deste produto.
		await disableProductStatuses(productName);

		const cachedOptions = beOptionsCache.current[productName];
		if (cachedOptions) {
			setBeModal({
				open: true,
				layerName: productName,
				options: cachedOptions,
				loading: false,
			});
			return;
		}

		setBeModal({
			open: true,
			layerName: productName,
			options: [],
			loading: true,
		});
		setLoadingLayers(prev => ({ ...prev, [checkboxKey]: true }));

		try {
			const options = await loadAvailableBes(productName, navigate, true);

			if (!activeBeFilterRef.current[productName]) return;

			if (options.length === 0) {
				toast.info('Nenhum BE encontrado para este produto.');
				deactivateBeFilter(productName);
				return;
			}

			beOptionsCache.current[productName] = options;
			setBeModal({
				open: true,
				layerName: productName,
				options,
				loading: false,
			});
		} catch (error) {
			console.error('Erro ao carregar BEs:', error);
			if (!error?.apiHandled) {
				toast.error('Não foi possível carregar os BEs.');
			}

			deactivateBeFilter(productName);
		} finally {
			setLoadingLayers(prev => {
				const updated = { ...prev };
				delete updated[checkboxKey];
				return updated;
			});
		}
	};

	const processBeFilterQueue = async productName => {
		if (beUpdateRunningRef.current[productName]) return;

		beUpdateRunningRef.current[productName] = true;
		setBeUpdatingLayers(prev => ({ ...prev, [productName]: true }));

		try {
			while (
				Object.prototype.hasOwnProperty.call(
					pendingBeSelectionRef.current,
					productName
				)
			) {
				const bes = pendingBeSelectionRef.current[productName];
				delete pendingBeSelectionRef.current[productName];

				if (!activeBeFilterRef.current[productName]) {
					removeBeFilterLayer(productName, map, loadedLayers);
					break;
				}

				const beLayerKey = getBeFilterLayerKey(productName);
				delete loadedBoundsCache.current[beLayerKey];

				if (!Array.isArray(bes) || bes.length === 0) {
					removeBeFilterLayer(productName, map, loadedLayers);
					continue;
				}

				await loadGeoJsonByBe(
					productName,
					bes,
					map,
					loadedLayers,
					navigate,
					false,
					openBottomDrawer,
					onPendingReviewOpen
				);

				if (!activeBeFilterRef.current[productName]) {
					removeBeFilterLayer(productName, map, loadedLayers);
					break;
				}
			}
		} finally {
			beUpdateRunningRef.current[productName] = false;
			setBeUpdatingLayers(prev => ({ ...prev, [productName]: false }));
		}
	};

	const handleBeSelectionChange = bes => {
		const productName = beModal.layerName;
		if (!productName || !activeBeFilterRef.current[productName]) return;

		setSelectedBesByLayer(prev => ({
			...prev,
			[productName]: bes,
		}));

		// Mantém apenas a seleção mais recente pendente. Se o usuário clicar
		// rapidamente em vários BEs, as requisições não ficam concorrendo.
		pendingBeSelectionRef.current[productName] = bes;
		processBeFilterQueue(productName);
	};

	const handleCloseBeFilter = () => {
		const productName = beModal.layerName;
		if (!productName) return;

		deactivateBeFilter(productName);
	};

	const handleCheckboxEvent = async (e, layerName, itemMeta = null) => {
		const isChecked = e.target.checked;
		const [parentName] = layerName.split('::');

		if (itemMeta?.type === 'be-filter') {
			await handleBeFilterToggle(e, parentName);
			return;
		}

		const parentConfig = getLayerConfig(parentName);
		const hasBeFilter = parentConfig?.children?.some(
			child => child.type === 'be-filter'
		);

		if (isChecked && hasBeFilter) {
			deactivateBeFilter(parentName);
		}

		// Bloqueia o checkbox
		setLoadingLayers(prev => {
			const updated = { ...prev, [layerName]: true };

			const layerGroup = getLayerConfig(layerName);
			if (layerGroup?.children) {
				layerGroup.children.forEach(child => {
					updated[`${layerName}::${child.name}`] = true;
				});
			}

			return updated;
		});

		const layerGroup = getLayerConfig(layerName);
		const normalChildren = getNormalChildren(layerGroup);
		const promises = [];

		if (!layerGroup?.children) {
			promises.push(
				handleCheckboxChange(
					e,
					map,
					loadedLayers,
					navigate,
					layerName,
					{ onPendingReviewOpen },
					false,
					enableDrawerToggle
				)
			);
		} else {
			const parentLayer = loadedLayers.current[layerName];
			if (parentLayer) {
				map.removeLayer(parentLayer);
				delete loadedLayers.current[layerName];
			}

			normalChildren.forEach(child => {
				const fullName = `${layerName}::${child.name}`;
				const fakeEvent = {
					target: { checked: isChecked },
					stopPropagation: () => {},
				};

				promises.push(
					handleCheckboxChange(
						fakeEvent,
						map,
						loadedLayers,
						navigate,
						fullName,
						{ silent: true, onPendingReviewOpen },
						true,
						openBottomDrawer
					)
				);
			});
		}
		await Promise.all(promises);

		setCheckedLayers(prev => {
			const updated = { ...prev, [layerName]: isChecked };

			if (layerGroup?.children) {
				normalChildren.forEach(child => {
					updated[`${layerName}::${child.name}`] = isChecked;
				});

				const beChild = layerGroup.children.find(
					child => child.type === 'be-filter'
				);
				if (beChild) {
					updated[`${layerName}::${beChild.name}`] = false;
				}
			}

			return updated;
		});

		setLoadingLayers(prev => {
			const updated = { ...prev };
			delete updated[layerName];

			if (layerGroup?.children) {
				layerGroup.children.forEach(child => {
					delete updated[`${layerName}::${child.name}`];
				});
			}

			return updated;
		});
	};

	useEffect(() => {
		document.body.classList.add('no-scroll');

		return () => {
			document.body.classList.remove('no-scroll');
		};
	}, []);

	useEffect(() => {
		ativarTodosSubLotes();
	}, [ativarTodosSubLotes]);

	useEffect(() => {
		handleBaseLayerChange(
			'OpenStreetMap',
			map,
			loadedLayers,
			selectedBaseLayer,
			setSelectedBaseLayer
		);
	}, []);

	useEffect(() => {
		const onMoveEnd = () => {
			const bounds = map.getBounds();

			Object.entries(loadedLayers.current).forEach(
				([layerKey, layer]) => {
					if (!(layer instanceof L.GeoJSON) || !map.hasLayer(layer))
						return;

					const name = layer.options?.layerName;
					const status = layer.options?.statusFilter || null;
					const beFilter = layer.options?.beFilter || [];

					if (!name) return;

					if (
						name === 'Blocos de Entrega' &&
						loadedLayers.current._blocosEntregaCarregados?.[status]
					) {
						return; // não recarrega esse lote
					}

					// Verifica se bound foi carregado antes
					const prevBounds = loadedBoundsCache.current[layerKey];
					if (prevBounds && prevBounds.contains(bounds)) return;

					// Atualiza o cache
					loadedBoundsCache.current[layerKey] = bounds;

					// só continue se o layer ainda está no mapa
					if (!map.hasLayer(layer)) return;

					loadGeoJson(
						name,
						map,
						loadedLayers,
						navigate,
						false,
						status,
						false,
						undefined,
						beFilter,
						onPendingReviewOpen
					);
				}
			);
		};

		map.on('moveend', onMoveEnd);
		map.on('zoomend', onMoveEnd);

		return () => {
			map.off('moveend', onMoveEnd);
			map.off('zoomend', onMoveEnd);
		};
	}, [map, navigate, selectedBaseLayer, onPendingReviewOpen]);

	useEffect(() => {
		// Auto-clique em Lotes do Projeto na inicialização
		const timer = setTimeout(() => {
			const checkboxes = document.querySelectorAll(
				'.painel-filtro input[type="checkbox"]'
			);
			checkboxesRef.current = Array.from(checkboxes);

			const lotesCheckbox = [...checkboxes].find(
				cb =>
					cb.closest('label')?.innerText.trim() === 'Lotes do Projeto'
			);
			if (lotesCheckbox && !lotesCheckbox.checked) {
				try {
					lotesCheckbox.click();
				} catch (error) {
					console.error(
						"Erro ao ativar camada 'Lotes do Projeto':",
						error
					);
				}
			}
		}, 600);

		return () => {
			clearTimeout(timer);
			checkboxesRef.current = [];
		};
	}, [map, navigate]);

	return (
		<>
			{window.innerWidth <= 768 && (
				<button
					className="toggle-panel-btn"
					onClick={() => setVisible(prev => !prev)}
				>
					{visible ? '✖' : '☰ Filtros'}
				</button>
			)}
			{visible && (
				<div className="painel-filtro" ref={panelRef}>
					<ToastContainer position="top-right" />

					{overLayers.map(({ group, layers }) => {
						const filteredLayers =
							isUserAdmin() || isContributorPerabiru()
								? layers
								: layers.filter(
										l => !ADMIN_ONLY_LAYERS.has(l.name)
									);

						if (!filteredLayers.length) return null;

						return (
							<OverlayGroup
								key={group}
								group={group}
								layers={filteredLayers}
								expandedGroups={expandedGroups}
								toggleGroup={toggleGroup}
								checkedLayers={checkedLayers}
								handleCheckboxEvent={handleCheckboxEvent}
								expandedSubmenus={expandedSubmenus}
								toggleSubmenu={toggleSubmenu}
								loadingLayers={loadingLayers}
							/>
						);
					})}
					<BaseLayer
						selectedBaseLayer={selectedBaseLayer}
						setSelectedBaseLayer={setSelectedBaseLayer}
						map={map}
						loadedLayers={loadedLayers}
						handleBaseLayerChange={handleBaseLayerChange}
					/>
					<QuickOverlayLayers
						checkedLayers={checkedLayers}
						handleCheckboxEvent={handleCheckboxEvent}
						loadingLayers={loadingLayers}
					/>
				</div>
			)}

			<BeFilterModal
				open={beModal.open}
				productName={beModal.layerName}
				options={beModal.options}
				selectedBes={
					beModal.layerName
						? selectedBesByLayer[beModal.layerName] || []
						: []
				}
				loading={beModal.loading}
				updating={
					beModal.layerName
						? beUpdatingLayers[beModal.layerName] || false
						: false
				}
				onSelectionChange={handleBeSelectionChange}
				onClose={handleCloseBeFilter}
			/>
		</>
	);
};

export default CustomPanel;
